import {Text} from '@dropbox/dig-components/typography';
import {EmployeeLdap, TeamWithCounts, Workstream, WorkstreamPrivacy} from 'client';
import {t} from 'i18next';
import {useEmployeeCount} from 'views/workstreams/WorkstreamPrivacyDrawer';

import styles from './WorkstreamDetailsShell.module.css';

const getAudienceSummary = (peopleCount: number, teamCount: number) => {
  const parts: string[] = [];

  if (peopleCount > 0) {
    parts.push(
      t('people_count_formatted', {
        count: peopleCount,
        countString: peopleCount.toLocaleString(),
      }).toString()
    );
  }

  if (teamCount > 0) {
    parts.push(t('team', {count: teamCount}).toString());
  }

  return parts.join(', ');
};

const dedupeTeams = (teams: TeamWithCounts[]) =>
  Array.from(new Map(teams.map((team) => [team.team_id, team])).values()).sort((left, right) =>
    (left.name ?? '').localeCompare(right.name ?? '')
  );

export const WorkstreamCustomAudienceSummary = ({
  workstream,
  privacy,
  className,
  wrapperClassName,
  inheritColor = false,
}: {
  workstream: Workstream;
  privacy: WorkstreamPrivacy;
  className?: string;
  wrapperClassName?: string;
  // Take the surrounding text color (e.g. inside a tooltip).
  inheritColor?: boolean;
}) => {
  const associatedPeople = [
    ...workstream.employee_associations.map(({employee}) => employee),
    ...((workstream.metrics?.map(({owner}) => owner).filter(Boolean) as EmployeeLdap[]) ?? []),
  ];
  const associatedPeopleIds = associatedPeople.map(({user_id}) => user_id);
  const privatePeople = workstream.private_employees ?? [];
  const privateTeams = dedupeTeams(workstream.private_teams ?? []);
  const peopleCount = useEmployeeCount(
    associatedPeopleIds,
    privatePeople.map(({user_id}) => user_id),
    privacy === WorkstreamPrivacy.CUSTOM,
    false
  );

  if (privacy !== WorkstreamPrivacy.CUSTOM) {
    return null;
  }

  const summary = getAudienceSummary(peopleCount, privateTeams.length);

  if (!summary) {
    return null;
  }

  const summaryContent = (
    <span
      className={wrapperClassName ?? styles.sidebarPrivacySummaryTrigger}
      data-testid="workstream-custom-audience-summary"
    >
      <Text size="small" className={className} color={inheritColor ? 'inherit' : undefined}>
        {summary}
      </Text>
    </span>
  );

  return summaryContent;
};
